/**
 * QuickBooks Export Section - Dashboard Component
 * Provides quick access to QuickBooks invoice and payroll exports
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Download,
  FileSpreadsheet,
  Calculator,
  Cloud,
  Monitor,
  Loader2,
  Receipt,
  DollarSign,
  ArrowRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function QuickBooksExportSection() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [exportType, setExportType] = useState("invoices"); // invoices or payroll
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState("");
  const [invoiceSummary, setInvoiceSummary] = useState(null);
  const [payrollSummary, setPayrollSummary] = useState(null);

  const fetchInvoiceSummary = async () => {
    if (!startDate || !endDate) {
      toast.error("Please select start and end dates");
      return;
    }
    try {
      setLoading("invoice-summary");
      const response = await axios.get(`${API}/payroll-export/quickbooks/invoices/summary`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setInvoiceSummary(response.data);
    } catch (error) {
      toast.error("Failed to fetch invoice summary");
    } finally {
      setLoading("");
    }
  };

  const fetchPayrollSummary = async () => {
    if (!startDate || !endDate) {
      toast.error("Please select start and end dates");
      return;
    }
    try {
      setLoading("payroll-summary");
      const response = await axios.get(`${API}/payroll-export/summary`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setPayrollSummary(response.data);
    } catch (error) {
      toast.error("Failed to fetch payroll summary");
    } finally {
      setLoading("");
    }
  };

  const handleExport = async (type, platform) => {
    if (!startDate || !endDate) {
      toast.error("Please select start and end dates");
      return;
    }

    const loadingKey = `${type}-${platform}`;
    setLoading(loadingKey);

    try {
      let endpoint = "";
      let filename = "";

      if (type === "invoices") {
        endpoint = platform === "online" 
          ? "/payroll-export/quickbooks/invoices/online"
          : "/payroll-export/quickbooks/invoices/desktop";
        filename = platform === "online"
          ? `quickbooks_invoices_${startDate}_${endDate}.csv`
          : `quickbooks_invoices_${startDate}_${endDate}.iif`;
      } else {
        endpoint = platform === "online"
          ? "/payroll-export/quickbooks/online"
          : "/payroll-export/quickbooks/desktop";
        filename = platform === "online"
          ? `quickbooks_payroll_${startDate}_${endDate}.csv`
          : `quickbooks_payroll_${startDate}_${endDate}.iif`;
      }

      const response = await axios.get(`${API}${endpoint}`, {
        params: { start_date: startDate, end_date: endDate },
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`QuickBooks ${type} export downloaded`);
    } catch (error) {
      toast.error(`Failed to export ${type}`);
    } finally {
      setLoading("");
    }
  };

  return (
    <>
      {/* Dashboard Card */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <FileSpreadsheet className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">QuickBooks</h2>
              <p className="text-xs text-slate-500">Export invoices & payroll</p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <Button
            onClick={() => { setExportType("invoices"); setDialogOpen(true); }}
            variant="outline"
            className="w-full justify-between group"
            data-testid="qb-invoices-btn"
          >
            <span className="flex items-center gap-2">
              <Receipt className="w-4 h-4 text-emerald-600" />
              Export Invoices
            </span>
            <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
          </Button>

          <Button
            onClick={() => { setExportType("payroll"); setDialogOpen(true); }}
            variant="outline"
            className="w-full justify-between group"
            data-testid="qb-payroll-btn"
          >
            <span className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-blue-600" />
              Export Payroll
            </span>
            <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
          </Button>
        </div>

        <div className="mt-4 pt-4 border-t">
          <Link 
            to="/hr-payroll?tab=payroll-export" 
            className="text-sm text-primary hover:underline flex items-center gap-1"
          >
            More export options (ADP, Paychex)
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      {/* Export Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
              QuickBooks {exportType === "invoices" ? "Invoice" : "Payroll"} Export
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            {/* Preview Summary Button */}
            <Button
              onClick={exportType === "invoices" ? fetchInvoiceSummary : fetchPayrollSummary}
              variant="outline"
              disabled={loading.includes("summary")}
              className="w-full"
            >
              {loading.includes("summary") ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Calculator className="w-4 h-4 mr-2" />
              )}
              Preview Summary
            </Button>

            {/* Invoice Summary */}
            {exportType === "invoices" && invoiceSummary && (
              <div className="p-4 bg-emerald-50 rounded-lg">
                <h4 className="font-medium text-emerald-800 mb-3">Invoice Summary</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Service Orders</p>
                    <p className="font-semibold text-lg">{invoiceSummary.service_order_count}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Transactions</p>
                    <p className="font-semibold text-lg">{invoiceSummary.transaction_count}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Service Revenue</p>
                    <p className="font-semibold text-lg text-emerald-600">
                      ${invoiceSummary.service_revenue?.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">Total Revenue</p>
                    <p className="font-semibold text-lg text-emerald-700">
                      ${invoiceSummary.total_revenue?.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Payroll Summary */}
            {exportType === "payroll" && payrollSummary && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-3">Payroll Summary</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Records</p>
                    <p className="font-semibold text-lg">{payrollSummary.record_count}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Regular Hours</p>
                    <p className="font-semibold text-lg">{payrollSummary.total_regular_hours}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Gross Pay</p>
                    <p className="font-semibold text-lg text-emerald-600">
                      ${payrollSummary.total_gross_pay?.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">Net Pay</p>
                    <p className="font-semibold text-lg text-blue-600">
                      ${payrollSummary.total_net_pay?.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Export Buttons */}
            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={() => handleExport(exportType, "online")}
                disabled={!!loading}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {loading === `${exportType}-online` ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Cloud className="w-4 h-4 mr-2" />
                )}
                QB Online (CSV)
              </Button>

              <Button
                onClick={() => handleExport(exportType, "desktop")}
                disabled={!!loading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading === `${exportType}-desktop` ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Monitor className="w-4 h-4 mr-2" />
                )}
                QB Desktop (IIF)
              </Button>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
